import React, { useEffect, useState } from 'react';
import { Button, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { PageContainer } from '../../components/shared/Page/PageContainer';
import { CardContainer } from '../../components/shared/CardContainer/CardContainer';
import { WorkflowsTable, GET_WORKFLOWS } from 'components/Workflows';

interface Workflow {
  id: string;
  name: string;
  active: boolean;
  description: string;
  json: any;
  createdOn: string;
  modifiedOn: string;
  lastModifiedBy: any;
}

const WorkflowsListPage: React.FC = () => {
  const history = useHistory();
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Workflow | undefined>('name');
  const [filteredRows, setFilteredRows] = useState<Workflow[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [rows, setRows] = useState<Workflow[]>([]); // Store the original rows

  const { loading, error } = useQuery(GET_WORKFLOWS, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data: any) => {
      if (data && data.workflows) {
        setRows(data.workflows);
        setFilteredRows(data.workflows);
      }
    },
  });

  useEffect(() => {
    const lowerCaseQuery = searchTerm.toLowerCase();
    setFilteredRows(
      rows.filter(
        row =>
          row.name.toLowerCase().includes(lowerCaseQuery) ||
          row.description.toLowerCase().includes(lowerCaseQuery) ||
          _.get(row, 'json.conditions', []).join(', ').toLowerCase().includes(lowerCaseQuery)
      )
    );
  }, [searchTerm, rows]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Workflow) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    const sortedRows = [...filteredRows].sort((a, b) => {
      const aValue = _.get(a, property, '');
      const bValue = _.get(b, property, '');

      if (aValue < bValue) return isAsc ? -1 : 1;
      if (aValue > bValue) return isAsc ? 1 : -1;
      return 0;
    });

    setFilteredRows(sortedRows);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (id: string) => {
    history.push(`/workflows/edit/${id}`);
  };

  return (
    <PageContainer loading={loading} error={error}>
      <CardContainer
        actionBtn={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push('/workflows/add')}>
            Add Workflow
          </Button>
        }
      >
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm}
          placeholder="Search"
          onChange={e => setSearchTerm(e.target.value)}
        />

        <WorkflowsTable
          workflows={filteredRows}
          page={page}
          rowsPerPage={rowsPerPage}
          order={order}
          orderBy={orderBy}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onRequestSort={handleRequestSort}
          onEdit={handleEdit}
        />
      </CardContainer>
    </PageContainer>
  );
};

export default WorkflowsListPage;
